import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  KeyboardEvent,
} from 'react';
import { Button, Select, DatePicker, Tooltip } from 'antd';
import {
  SearchOutlined,
  CloseOutlined,
  CloseCircleFilled,
} from '@ant-design/icons';
import {
  SearchToken,
  ClauseToken,
  Operator,
  ComparisonOperator,
  COMPARISON_LABELS,
  OPERATOR_LABELS,
  serializeTokens,
  parseSearchString,
  normalizeTokens,
  canAppendOperator,
  sanitizeValue,
  sanitizeProValue,
  isNullaryComparison,
} from 'helpers/searchGrammar';
import {
  isClause,
  displayValues,
  descriptorFor,
} from 'helpers/advancedSearchBar';
import { useSearchSchema } from 'hooks/useSearchSchema';
import {
  FieldDescriptor,
  FieldOption,
} from 'types/advancedSearch';
import './AdvancedSearchBar.less';

const { Option } = Select;
const OPERATORS: Operator[] = ['AND', 'OR', 'XOR'];
const OPERATOR_WORDS: Record<string, Operator> = { and: 'AND', or: 'OR', xor: 'XOR' };
const ASYNC_DEBOUNCE_MS = 300;

interface AdvancedSearchBarProps {
  value: string;
  onSearch: (value: string) => void;
  /** Registry-driven: fields, options and autocomplete all come from the API. */
  model?: string;
  /** Inline alternative to `model` — page-owned fields, no backend registry. */
  fields?: FieldDescriptor[];
  placeholder?: string;
  loading?: boolean;
  /** Off for backends whose filters are AND-only, so OR/XOR can't be offered. */
  allowOperators?: boolean;
  /**
   * Turns the bar into a value picker: the menu lists matching values for what
   * has been typed instead of attributes, operators and free text.
   */
  suggest?: (query: string) => Promise<string[]>;
}

interface DraftControlProps {
  descriptor: FieldDescriptor;
  onCommit: (values: string[]) => void;
  onCancel: () => void;
  initialValues?: string[];
  onNavigate?: (direction: -1 | 1) => void;
  /** Reports what is picked/typed so far, so leaving the draft can keep it. */
  onPendingChange?: (values: string[]) => void;
}

function DraftControl({
  descriptor,
  onCommit,
  onCancel,
  initialValues,
  onNavigate,
  onPendingChange,
}: DraftControlProps) {
  const [text, setText] = useState(initialValues?.[0] ?? '');
  const [selected, setSelected] = useState<string[]>(initialValues ?? []);
  const [asyncOptions, setAsyncOptions] = useState<FieldOption[]>([]);
  const [asyncLoading, setAsyncLoading] = useState(false);
  const [asyncQuery, setAsyncQuery] = useState('');
  const [typed, setTyped] = useState('');
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const textRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const input = textRef.current;
    if (!input) return;
    const end = input.value.length;
    input.setSelectionRange(end, end);
  }, []);

  const runAsyncSearch = (query: string) => {
    setAsyncQuery(query);
    if (!descriptor.asyncSearch) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    if (!query || query.length < 2) {
      setAsyncOptions([]);
      setAsyncLoading(false);
      return;
    }
    timerRef.current = setTimeout(async () => {
      setAsyncLoading(true);
      try {
        setAsyncOptions(await descriptor.asyncSearch!(query));
      } catch {
        setAsyncOptions([]);
      } finally {
        setAsyncLoading(false);
      }
    }, ASYNC_DEBOUNCE_MS);
  };

  const commitText = () => {
    const clean = sanitizeValue(text);
    if (clean) onCommit([clean]);
  };

  const commitSelected = () => {
    if (selected.length) onCommit(selected);
    else onCancel();
  };

  const changeSelected = (v: any) => {
    const values: string[] = Array.isArray(v) ? v : [v];
    setSelected(values);
    setTyped('');
    onPendingChange?.(values);
  };

  // Multi-pickers commit when you move on: Enter (with nothing typed — a typed
  // query's Enter picks the highlighted option instead), Tab, or a click
  // outside the bar. Escape is the only way to drop the picks.
  const multiKeyDown = (e: any) => {
    if (navigateFromPicker(e)) return;
    if (e.key === 'Escape') onCancel();
    if (e.key === 'Tab' || (e.key === 'Enter' && !typed)) {
      e.preventDefault();
      commitSelected();
    }
  };

  const navigateFromPicker = (e: any) => {
    if (typed) return false;
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      onNavigate?.(-1);
      return true;
    }
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      onNavigate?.(1);
      return true;
    }
    return false;
  };

  switch (descriptor.kind) {
    case 'boolean':
      return (
        <Select
          className="asb__draft-control asb__draft-control--boolean"
          autoFocus
          defaultOpen
          size="small"
          bordered={false}
          placeholder="true / false"
          defaultValue={initialValues?.[0]}
          onSelect={(v: string) => onCommit([v])}
          onInputKeyDown={e => {
            if (navigateFromPicker(e)) return;
            if (e.key === 'Escape') onCancel();
          }}
        >
          <Option value="true">{descriptor.trueLabel ?? 'True'}</Option>
          <Option value="false">{descriptor.falseLabel ?? 'False'}</Option>
        </Select>
      );

    case 'date':
      return (
        <DatePicker
          className="asb__draft-control asb__draft-control--date"
          autoFocus
          open
          size="small"
          bordered={false}
          format={descriptor.dateFormat}
          onKeyDown={e => {
            if (e.key === 'Escape') onCancel();
          }}
          onChange={val => {
            if (!val) return;
            onCommit([descriptor.formatDate ? descriptor.formatDate(val) : String(val)]);
          }}
        />
      );

    case 'enum': {
      const multiple = !!descriptor.multiple;
      // Filter against plain text even when the option label is a rich node.
      const searchTextByValue = new Map(
        (descriptor.options ?? []).map(o => [
          o.value,
          (o.searchText ?? (typeof o.label === 'string' ? o.label : o.value)).toLowerCase(),
        ]),
      );
      return (
        <span className="asb__draft-control-wrap">
          <Select
            className="asb__draft-control asb__draft-control--enum"
            autoFocus
            defaultOpen
            size="small"
            bordered={false}
            showSearch
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 320 }}
            mode={multiple ? 'multiple' : undefined}
            placeholder={descriptor.placeholder ?? `Select ${descriptor.label}`}
            loading={descriptor.loading}
            defaultValue={(multiple ? initialValues : initialValues?.[0]) as any}
            onSearch={setTyped}
            filterOption={(input, option) =>
              (searchTextByValue.get(option?.value as string) ?? '').includes(input.toLowerCase())
            }
            onSelect={(v: string) => !multiple && onCommit([v])}
            onChange={(v: any) => multiple && changeSelected(v)}
            onInputKeyDown={e => {
              if (multiple) {
                multiKeyDown(e);
                return;
              }
              if (navigateFromPicker(e)) return;
              if (e.key === 'Escape') onCancel();
            }}
            options={descriptor.options?.map(o => ({ value: o.value, label: o.label }))}
          />
        </span>
      );
    }

    case 'async': {
      const multiple = !!descriptor.asyncMultiple;
      const trimmedQuery = asyncQuery.trim();
      const { emptyOption } = descriptor;
      const suggestions = [
        ...(emptyOption?.value.startsWith(trimmedQuery.toLowerCase()) ? [emptyOption] : []),
        ...asyncOptions,
      ];
      const hasExactSuggestion = suggestions.some(o => String(o.value) === trimmedQuery);
      const asyncSelectOptions = [
        ...(trimmedQuery && !hasExactSuggestion
          ? [{ value: trimmedQuery, label: `Contains “${trimmedQuery}”` }]
          : []),
        ...suggestions.map(o => ({ value: o.value, label: o.label })),
      ];
      return (
        <span className="asb__draft-control-wrap">
          <Select
            className="asb__draft-control asb__draft-control--async"
            autoFocus
            defaultOpen
            size="small"
            bordered={false}
            showSearch
            filterOption={false}
            defaultActiveFirstOption
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ minWidth: 320 }}
            mode={multiple ? 'multiple' : undefined}
            placeholder={descriptor.placeholder ?? `Search ${descriptor.label}…`}
            notFoundContent={asyncLoading ? 'Searching…' : 'Type to search'}
            loading={asyncLoading}
            defaultValue={(multiple ? initialValues : initialValues?.[0]) as any}
            onSearch={query => {
              setTyped(query);
              runAsyncSearch(query);
            }}
            onSelect={(v: string) => !multiple && onCommit([v])}
            onChange={(v: any) => multiple && changeSelected(v)}
            onInputKeyDown={e => {
              if (multiple) {
                multiKeyDown(e);
                return;
              }
              if (navigateFromPicker(e)) return;
              if (e.key === 'Escape') onCancel();
            }}
            options={asyncSelectOptions}
          />
        </span>
      );
    }

    case 'text':
    default:
      return (
        <input
          ref={textRef}
          className="asb__draft-control asb__draft-control--text"
          autoFocus
          autoComplete="off"
          data-form-type="other"
          placeholder={descriptor.placeholder ?? 'Enter value…'}
          value={text}
          onChange={e => {
            setText(e.target.value);
            onPendingChange?.([e.target.value]);
          }}
          onKeyDown={e => {
            const input = e.currentTarget;
            const atStart = input.selectionStart === 0 && input.selectionEnd === 0;
            const atEnd = input.selectionStart === text.length && input.selectionEnd === text.length;
            if (e.key === 'ArrowLeft' && atStart) {
              e.preventDefault();
              onNavigate?.(-1);
              return;
            }
            if (e.key === 'ArrowRight' && atEnd) {
              e.preventDefault();
              onNavigate?.(1);
              return;
            }
            if (e.key === 'Enter') {
              e.preventDefault();
              commitText();
            }
            if (e.key === 'Escape') onCancel();
          }}
        />
      );
  }
}

// ── main bar ────────────────────────────────────────────────────────────────

function AdvancedSearchBar({
  value,
  onSearch,
  model,
  fields,
  placeholder = 'Search…',
  loading,
  allowOperators = true,
  suggest,
}: AdvancedSearchBarProps) {
  const { descriptors: modelDescriptors, isLoading: schemaLoading } =
    useSearchSchema(model ?? '', !!model);
  const descriptors = useMemo(
    () => (model ? modelDescriptors : fields ?? []),
    [model, modelDescriptors, fields],
  );
  const isLoading = loading || schemaLoading;

  const [tokens, setTokens] = useState<SearchToken[]>(() => parseSearchString(value || ''));
  const [inputText, setInputText] = useState('');
  const [draftDescriptor, setDraftDescriptor] = useState<FieldDescriptor | null>(null);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [suggestLoading, setSuggestLoading] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ left: number; top: number }>({ left: 0, top: 0 });
  const [activeIndex, setActiveIndex] = useState(-1);

  const barRef = useRef<HTMLDivElement>(null);
  const fieldRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const activeItemRef = useRef<HTMLButtonElement>(null);
  const refocusRef = useRef(false);
  const refocusOpensMenuRef = useRef(true);
  // What the open draft/edit control holds so far; see keepPendingDraft.
  const pendingValuesRef = useRef<string[]>([]);
  const keepPendingDraftRef = useRef<() => void>(() => {});

  const MENU_WIDTH = 320;

  function updateMenuPos() {
    const bar = barRef.current;
    const input = inputRef.current;
    if (!bar || !input) return;
    const barRect = bar.getBoundingClientRect();
    const inputRect = input.getBoundingClientRect();
    const rawLeft = inputRect.left - barRect.left;
    const maxLeft = Math.max(0, bar.clientWidth - MENU_WIDTH - 4);
    setMenuPos({ left: Math.min(Math.max(0, rawLeft), maxLeft), top: inputRect.bottom - barRect.top + 6 });
  }

  useLayoutEffect(() => {
    if (menuOpen && !draftDescriptor) updateMenuPos();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuOpen, draftDescriptor, tokens, inputText]);

  const wireKeyOf = (d: FieldDescriptor) => d.wireKey ?? d.label;

  const descriptorByWireKey = useMemo(() => {
    const map = new Map<string, FieldDescriptor>();
    descriptors.forEach(d => map.set(wireKeyOf(d), d));
    return map;
  }, [descriptors]);

  useEffect(() => {
    if (value !== serializeTokens(tokens)) {
      setTokens(parseSearchString(value || ''));
      setDraftDescriptor(null);
      setEditingIndex(null);
      setInputText('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  useEffect(() => {
    pendingValuesRef.current = [];
  }, [draftDescriptor, editingIndex]);

  useEffect(() => {
    function onDocMouseDown(e: MouseEvent) {
      const target = e.target as HTMLElement;
      if (barRef.current?.contains(target)) return;
      if (target?.closest?.('.ant-select-dropdown, .ant-picker-dropdown')) return;
      keepPendingDraftRef.current();
      setMenuOpen(false);
      setDraftDescriptor(null);
      setEditingIndex(null);
    }
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  // Value suggestions replace the attribute menu entirely when `suggest` is set.
  useEffect(() => {
    if (!suggest) return;
    const query = inputText.trim();
    if (!query) {
      setSuggestions([]);
      setSuggestLoading(false);
      return;
    }
    setSuggestLoading(true);
    const timer = setTimeout(async () => {
      try {
        setSuggestions(await suggest(query));
      } catch {
        setSuggestions([]);
      } finally {
        setSuggestLoading(false);
      }
    }, ASYNC_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [suggest, inputText]);

  const filteredDescriptors = useMemo(() => {
    const q = inputText.trim().toLowerCase();
    if (!q) return descriptors;
    return descriptors.filter(d => d.label.toLowerCase().includes(q));
  }, [descriptors, inputText]);

  const editing = editingIndex !== null;
  const showOperatorSection =
    allowOperators && !suggest && canAppendOperator(tokens) && !draftDescriptor;

  type MenuItem =
    | { type: 'freetext' }
    | { type: 'operator'; op: Operator }
    | { type: 'attribute'; descriptor: FieldDescriptor }
    | { type: 'suggestion'; value: string };

  const menuItems = useMemo<MenuItem[]>(() => {
    if (suggest) return suggestions.map(value => ({ type: 'suggestion', value }));
    const items: MenuItem[] = [];
    if (inputText.trim()) items.push({ type: 'freetext' });
    if (showOperatorSection) OPERATORS.forEach(op => items.push({ type: 'operator', op }));
    filteredDescriptors.forEach(descriptor => items.push({ type: 'attribute', descriptor }));
    return items;
  }, [suggest, suggestions, inputText, showOperatorSection, filteredDescriptors]);

  // Section offsets derived from the same order menuItems is built in, so the
  // keyboard index and the rendered rows can never drift apart.
  const freeTextIndex = !suggest && inputText.trim() ? 0 : -1;
  const operatorBase = inputText.trim() ? 1 : 0;
  const attributeBase = operatorBase + (showOperatorSection ? OPERATORS.length : 0);

  function requestFocus({ openMenu = true } = {}) {
    setActiveIndex(-1);
    if (openMenu) setMenuOpen(true);
    if (inputRef.current) {
      refocusRef.current = false;
      inputRef.current.focus();
      return;
    }
    refocusRef.current = true;
    refocusOpensMenuRef.current = openMenu;
  }

  function activateMenuItem(index: number) {
    const item = menuItems[index];
    if (!item) return;
    if (item.type === 'freetext') submit();
    else if (item.type === 'operator') chooseOperator(item.op);
    else if (item.type === 'suggestion') chooseSuggestion(item.value);
    else chooseField(item.descriptor);
  }

  useEffect(() => {
    if (draftDescriptor || editingIndex !== null) return;
    if (!refocusRef.current) return;
    refocusRef.current = false;
    inputRef.current?.focus();
    if (refocusOpensMenuRef.current) setMenuOpen(true);
    setActiveIndex(-1);
  }, [draftDescriptor, editingIndex]);

  useEffect(() => {
    setActiveIndex(prev => (prev >= menuItems.length ? menuItems.length - 1 : prev));
  }, [menuItems.length]);

  useEffect(() => {
    activeItemRef.current?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  function appendClause(prev: SearchToken[], clause: ClauseToken): SearchToken[] {
    const next = [...prev];
    if (next.length && next[next.length - 1].type === 'clause') {
      next.push({ type: 'operator', operator: 'AND' });
    }
    next.push(clause);
    return next;
  }

  function chooseField(descriptor: FieldDescriptor) {
    if (descriptor.disabled) return;
    setDraftDescriptor(descriptor);
    setEditingIndex(null);
    setInputText('');
    setMenuOpen(false);
    setActiveIndex(-1);
  }

  function chooseOperator(operator: Operator) {
    setTokens(prev => (canAppendOperator(prev) ? [...prev, { type: 'operator', operator }] : prev));
    setInputText('');
    requestFocus();
  }

  function commitDraft(values: string[], refocus = true) {
    if (!draftDescriptor) return;
    const clean = values.map(sanitizeValue).filter(Boolean);
    if (!clean.length) {
      setDraftDescriptor(null);
      if (refocus) requestFocus();
      return;
    }
    // A new chip runs the search straight away, the same as removing or editing
    // one does — otherwise the list sits unchanged until the user guesses Enter.
    const next = appendClause(tokens, { type: 'clause', field: wireKeyOf(draftDescriptor), values: clean });
    setTokens(next);
    setDraftDescriptor(null);
    setInputText('');
    onSearch(serializeTokens(next));
    if (refocus) requestFocus();
  }

  /** Replace a committed clause's values in place and re-run the search. */
  function commitEdit(index: number, values: string[], refocus = true) {
    const token = tokens[index];
    if (!token || !isClause(token)) return;
    const clean = values
      .map(v => (token.comparison ? sanitizeProValue(v) : sanitizeValue(v)))
      .filter(Boolean);
    if (!clean.length) return;
    const next = tokens.map((t, i) => (i === index ? { ...(t as ClauseToken), values: clean } : t));
    setTokens(next);
    setEditingIndex(null);
    onSearch(serializeTokens(next));
    if (refocus) requestFocus();
  }

  /**
   * Clicking away from an open draft keeps what was picked instead of throwing
   * it away. Read through a ref because the outside-click listener is bound once.
   */
  keepPendingDraftRef.current = () => {
    const pending = pendingValuesRef.current;
    if (!pending.length) return;
    if (draftDescriptor) commitDraft(pending, false);
    else if (editingIndex !== null) commitEdit(editingIndex, pending, false);
  };

  function setOperatorAt(index: number, operator: Operator) {
    const next = tokens.map((t, i) =>
      i === index && t.type === 'operator' ? ({ type: 'operator', operator } as SearchToken) : t,
    );
    setTokens(next);
    setEditingIndex(null);
    onSearch(serializeTokens(next));
    requestFocus();
  }

  /**
   * Walk between tags. Stepping past the last one returns to the main input;
   * stepping left off the first one stays put rather than trapping focus.
   */
  function moveFocus(fromIndex: number, direction: -1 | 1) {
    const next = fromIndex + direction;
    if (next < 0) return;
    if (next >= tokens.length) {
      setEditingIndex(null);
      requestFocus();
      return;
    }
    setEditingIndex(next);
  }

  /** Picking a suggestion replaces the query outright — the bar holds one value. */
  function chooseSuggestion(value: string) {
    const next: SearchToken[] = [{ type: 'clause', values: [value] }];
    setTokens(next);
    setInputText('');
    setSuggestions([]);
    setMenuOpen(false);
    setActiveIndex(-1);
    onSearch(value);
  }

  function startEditing(index: number) {
    setDraftDescriptor(null);
    setMenuOpen(false);
    setEditingIndex(index);
  }

  function stopEditing() {
    setEditingIndex(null);
    requestFocus();
  }

  function removeTokenAt(index: number) {
    const next = normalizeTokens(tokens.filter((_, i) => i !== index));
    setTokens(next);
    setEditingIndex(null);
    onSearch(serializeTokens(next));
    requestFocus({ openMenu: false });
  }

  function removeLastToken() {
    if (!tokens.length) return;
    let next = tokens.slice(0, -1);
    if (next.length && next[next.length - 1].type === 'operator') next = next.slice(0, -1);
    setTokens(next);
    onSearch(serializeTokens(next));
  }

  function clearAll() {
    setTokens([]);
    setInputText('');
    setDraftDescriptor(null);
    setEditingIndex(null);
    setMenuOpen(false);
    onSearch('');
    requestFocus({ openMenu: false });
  }

  function submit() {
    let finalTokens = tokens;
    const clean = inputText.trim();
    if (!draftDescriptor && clean) {
      finalTokens = appendClause(tokens, { type: 'clause', values: [clean] });
      setTokens(finalTokens);
      setInputText('');
    }
    setMenuOpen(false);
    setDraftDescriptor(null);
    setEditingIndex(null);
    onSearch(serializeTokens(finalTokens));
    requestFocus({ openMenu: false });
  }

  function onInputKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setMenuOpen(true);
      setActiveIndex(i => Math.min(i + 1, menuItems.length - 1));
      return;
    }
    if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex(i => Math.max(i - 1, 0));
      return;
    }
    if (e.key === 'Enter') {
      e.preventDefault();
      // A highlighted row wins: pick that attribute / operator / free-text search.
      if (menuOpen && activeIndex >= 0 && activeIndex < menuItems.length) {
        activateMenuItem(activeIndex);
        return;
      }
      const word = inputText.trim().toLowerCase();
      // Free-typed operator (AND/OR/XOR) — only valid right after a clause.
      if (allowOperators && OPERATOR_WORDS[word] && canAppendOperator(tokens)) {
        chooseOperator(OPERATOR_WORDS[word]);
        return;
      }
      // Otherwise Enter always runs the search (committing any pending free text).
      submit();
    } else if (e.key === 'ArrowLeft' && !inputText && tokens.length && e.currentTarget.selectionStart === 0) {
      // Step back into the last tag and land inside its value.
      e.preventDefault();
      setMenuOpen(false);
      startEditing(tokens.length - 1);
    } else if (e.key === 'Backspace' && !inputText && tokens.length) {
      removeLastToken();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      // First Esc closes the menu, the next one clears the query.
      if (menuOpen) {
        setMenuOpen(false);
        setActiveIndex(-1);
      } else if (tokens.length || inputText) {
        clearAll();
      }
    }
  }

  const hasContent = tokens.length > 0 || inputText.length > 0 || !!draftDescriptor;
  // Pages with no searchable attributes are pure free-text search — don't pop an
  // empty "No attributes" menu; only open when there's actually something to show.
  const hasAttributes = descriptors.length > 0;
  const hasMenuContent = suggest
    ? !!inputText.trim()
    : !!inputText.trim() || showOperatorSection || hasAttributes;

  function renderClauseChip(token: ClauseToken, index: number) {
    const descriptor = descriptorFor(token, descriptorByWireKey);
    const isEditing = editingIndex === index;

    if (isEditing) {
      return (
        <span key={`cl-${index}`} className="asb__chip asb__chip--editing">
          {token.field && <span className="asb__chip-key">{descriptor.label}</span>}
          <DraftControl
            key={`edit-${index}`}
            descriptor={descriptor}
            initialValues={token.values}
            onCommit={values => commitEdit(index, values)}
            onPendingChange={values => (pendingValuesRef.current = values)}
            onCancel={stopEditing}
            onNavigate={direction => moveFocus(index, direction)}
          />
        </span>
      );
    }

    return (
      <span
        key={`cl-${index}`}
        className={`asb__chip${token.comparison ? ' asb__chip--pro' : ''}`}
        title="Click to edit"
      >
        {token.field && <span className="asb__chip-key">{descriptor.label}</span>}
        {token.comparison && (
          <span className="asb__chip-op">{COMPARISON_LABELS[token.comparison]}</span>
        )}
        <button
          type="button"
          className="asb__chip-val"
          onClick={e => {
            e.stopPropagation();
            startEditing(index);
          }}
        >
          {isNullaryComparison(token.comparison)
            ? '—'
            : displayValues(token, descriptorByWireKey.get(token.field ?? ''))}
        </button>
        <button
          type="button"
          className="asb__chip-remove"
          aria-label="Remove filter"
          onClick={e => {
            e.stopPropagation();
            removeTokenAt(index);
          }}
        >
          <CloseOutlined />
        </button>
      </span>
    );
  }

  function renderOperatorChip(token: SearchToken & { type: 'operator' }, index: number) {
    if (editingIndex === index) {
      // An operator tag has no value to type into, so stepping onto it opens its
      // choices straight away.
      return (
        <span
          key={`op-${index}`}
          className="asb__operator asb__operator--editing"
          onKeyDown={e => {
            if (e.key === 'ArrowLeft') {
              e.preventDefault();
              moveFocus(index, -1);
            }
            if (e.key === 'ArrowRight') {
              e.preventDefault();
              moveFocus(index, 1);
            }
            if (e.key === 'Escape') stopEditing();
          }}
        >
          <Select<Operator>
            className="asb__operator-select"
            autoFocus
            defaultOpen
            size="small"
            bordered={false}
            value={token.operator}
            dropdownMatchSelectWidth={false}
            onChange={operator => setOperatorAt(index, operator)}
            options={OPERATORS.map(op => ({ value: op, label: OPERATOR_LABELS[op] }))}
          />
        </span>
      );
    }

    return (
      <button
        type="button"
        key={`op-${index}`}
        className="asb__operator"
        title="Click to change operator (AND / OR / XOR)"
        onClick={e => {
          e.stopPropagation();
          startEditing(index);
        }}
      >
        {OPERATOR_LABELS[token.operator]}
      </button>
    );
  }

  return (
    <div className={`asb${isLoading ? ' asb--loading' : ''}`} ref={barRef}>
      <SearchOutlined className="asb__lead-icon" />

      <div
        className="asb__field"
        ref={fieldRef}
        onClick={e => {
          // Clicking the bar beside an open draft also means "done with it".
          const target = e.target as HTMLElement;
          if (!target.closest('.asb__chip--draft, .asb__chip--editing')) {
            keepPendingDraftRef.current();
          }
          requestFocus();
        }}
      >
        {tokens.map((token, index) =>
          token.type === 'operator' ? renderOperatorChip(token, index) : renderClauseChip(token, index),
        )}

        {draftDescriptor ? (
          <span className="asb__chip asb__chip--draft">
            <span className="asb__chip-key">{draftDescriptor.label}</span>
            <DraftControl
              descriptor={draftDescriptor}
              onCommit={values => commitDraft(values)}
              onPendingChange={values => (pendingValuesRef.current = values)}
              onCancel={() => {
                setDraftDescriptor(null);
                requestFocus();
              }}
            />
          </span>
        ) : (
          !editing && (
            <input
              ref={inputRef}
              className="asb__input"
              placeholder={tokens.length ? 'Filter or search…' : placeholder}
              value={inputText}
              autoComplete="off"
              data-form-type="other"
              onFocus={() => setMenuOpen(true)}
              onChange={e => {
                const raw = e.target.value;
                // "and " / "or " / "xor " typed right after a clause → operator token.
                const opMatch = allowOperators ? raw.match(/^\s*(and|or|xor)\s+$/i) : null;
                if (opMatch && canAppendOperator(tokens)) {
                  chooseOperator(OPERATOR_WORDS[opMatch[1].toLowerCase()]);
                  return;
                }
                setInputText(raw);
                setMenuOpen(true);
                setActiveIndex(-1);
              }}
              onKeyDown={onInputKeyDown}
            />
          )
        )}
      </div>

      {menuOpen && !draftDescriptor && !editing && hasMenuContent && (
        <div
          className="asb__menu"
          style={{ left: menuPos.left, top: menuPos.top }}
          onMouseDown={e => e.preventDefault()}
        >
          {suggest && (
            <div className="asb__menu-section">
              <div className="asb__menu-title">Clusters</div>
              <div className="asb__menu-list">
                {suggestions.map((value, i) => {
                  const active = activeIndex === i;
                  return (
                    <button
                      key={value}
                      type="button"
                      ref={active ? activeItemRef : undefined}
                      className={`asb__menu-item${active ? ' asb__menu-item--active' : ''}`}
                      onMouseEnter={() => setActiveIndex(i)}
                      onMouseDown={e => e.preventDefault()}
                      onClick={() => chooseSuggestion(value)}
                    >
                      {value}
                    </button>
                  );
                })}
                {!suggestions.length && (
                  <div className="asb__menu-empty">
                    {suggestLoading ? 'Searching…' : 'No matching cluster'}
                  </div>
                )}
              </div>
            </div>
          )}

          {!suggest && inputText.trim() && (
            <button
              type="button"
              ref={activeIndex === freeTextIndex ? activeItemRef : undefined}
              className={`asb__menu-freetext${activeIndex === freeTextIndex ? ' asb__menu-freetext--active' : ''}`}
              onMouseEnter={() => setActiveIndex(freeTextIndex)}
              onClick={submit}
            >
              <SearchOutlined /> Search for <strong>“{inputText.trim()}”</strong>
            </button>
          )}

          {showOperatorSection && (
            <div className="asb__menu-section">
              <div className="asb__menu-title">Operators</div>
              <div className="asb__menu-operators">
                {OPERATORS.map((op, i) => {
                  const idx = operatorBase + i;
                  const active = activeIndex === idx;
                  return (
                    <button
                      type="button"
                      key={op}
                      ref={active ? activeItemRef : undefined}
                      className={`asb__menu-operator${active ? ' asb__menu-operator--active' : ''}`}
                      onMouseEnter={() => setActiveIndex(idx)}
                      onClick={() => chooseOperator(op)}
                    >
                      {OPERATOR_LABELS[op]}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {hasAttributes && (
            <div className="asb__menu-section">
              <div className="asb__menu-title">Attributes for filters</div>
              <div className="asb__menu-list">
                {filteredDescriptors.length ? (
                  filteredDescriptors.map((d, i) => {
                    const idx = attributeBase + i;
                    const active = activeIndex === idx;
                    return (
                      <button
                        type="button"
                        key={d.label}
                        ref={active ? activeItemRef : undefined}
                        className={`asb__menu-item${active ? ' asb__menu-item--active' : ''}`}
                        disabled={d.disabled}
                        onMouseEnter={() => setActiveIndex(idx)}
                        onClick={() => chooseField(d)}
                      >
                        {d.label}
                      </button>
                    );
                  })
                ) : (
                  <div className="asb__menu-empty">No matching attribute — press Enter to search</div>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="asb__actions">
        {hasContent && (
          <Tooltip title="Clear">
            <CloseCircleFilled className="asb__clear" onClick={clearAll} />
          </Tooltip>
        )}
        <Button type="primary" size="small" icon={<SearchOutlined />} onClick={submit}>
          Search
        </Button>
      </div>
    </div>
  );
}

export default AdvancedSearchBar;
